/**
 * Unify Task model — comprehensive FHIR schema.
 *
 * Tasks represent follow-up items, screening requests, documentation
 * actions, and supervision workflow items (missing notes, cosign reviews).
 *
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ Field              │ Type                     │ Description                   │
 * ├──────────────────────────────────────────────────────────────────────────────┤
 * │ status             │ code                     │ requested|in-progress|completed│
 * │ intent             │ code                     │ Always "order"                │
 * │ priority           │ code                     │ routine|urgent|asap|stat      │
 * │ code.coding[]      │ Coding[]                 │ Task type + supervision codes │
 * │ code.text          │ string                   │ Display text for the code     │
 * │ description        │ string                   │ Human-readable task text      │
 * │ for                │ Reference<Patient>       │ Patient this task is about    │
 * │ subject            │ Reference<Patient>       │ Same as `for` (subject)       │
 * │ owner              │ Reference<Practitioner>  │ Practitioner assigned         │
 * │ requester          │ Reference<Practitioner>  │ Who created the task          │
 * │ focus              │ Reference                │ Resource the task is about    │
 * │ authoredOn         │ dateTime                 │ When task was created         │
 * │ restriction.period │ Period                   │ Due date (period.end)         │
 * │ note               │ Annotation[]             │ Additional notes              │
 * │ meta.tag[]         │ Coding[]                 │ Supervision category tag      │
 * │ extension[]        │ Extension[]              │ EpisodeOfCare context         │
 * └──────────────────────────────────────────────────────────────────────────────┘
 *
 * code.coding systems:
 *  - TASK_TYPE_SYSTEM                     → general task codes (form, send-email-notification, etc.)
 *  - SUPERVISION_TASK_SYSTEM              → supervision-specific codes (missing-notes, missing-forms, etc.)
 *  - SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM → form subtype (progress-note, intake-note, etc.)
 *
 * meta.tag systems:
 *  - SUPERVISION_TASK_CATEGORY_SYSTEM → "missing-document" category marker
 *
 * Extensions:
 *  - EPISODE_OF_CARE_CONTEXT_EXT → valueReference linking task to an EpisodeOfCare
 */

import type { Task, Reference, Extension } from '@medplum/fhirtypes';
import { withUnifyProfile } from '../profiles/with-unify-profile';

import {
  TASK_TYPE_SYSTEM,
  TASK_CATEGORY_SYSTEM,
  TASK_LIFECYCLE_SYSTEM,
  TASK_LIFECYCLE_SYSTEM_MANAGED_CODE,
  NOTIFICATION_CATEGORY_SYSTEM,
  CLIENT_FACING_TASK_TAG_SYSTEM,
  CLIENT_FACING_TASK_TAG_CODE,
  SUPERVISION_TASK_SYSTEM,
  SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM,
  SUPERVISION_TASK_CATEGORY_SYSTEM,
  EPISODE_OF_CARE_CONTEXT_EXT,
  MISSING_NOTE_TASK_CODE,
  COSIGN_REQUIRED_TASK_CODE,
  SUPERVISOR_EDIT_REQUESTED_TASK_CODE,
  TREATMENT_PLAN_DUE_TASK_CODE,
  TREATMENT_PLAN_RENEWAL_TASK_CODE,
  APPOINTMENT_REQUEST_TASK_CODE,
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_DOCUMENT_COMPLETION_CODE,
  CLAIM_ERROR_TASK_CODE,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
//  Coded value types
// ═══════════════════════════════════════════════════════════════════════════

/** Task.status values used by Unify. */
export type TaskStatus = 'draft' | 'requested' | 'received' | 'accepted' | 'in-progress' | 'completed' | 'cancelled' | 'rejected';

/** Task.intent values used by Unify (always "order" in practice). */
export type TaskIntent = 'order' | 'proposal' | 'plan';

/** Task.priority values. */
export type TaskPriority = 'routine' | 'urgent' | 'asap' | 'stat';

/** General task type codes (TASK_TYPE_SYSTEM). */
export type TaskTypeCode =
  | 'form'
  | 'send-email-notification'
  | 'send-sms-notification'
  | 'billing-exception'
  | typeof MISSING_NOTE_TASK_CODE
  | typeof COSIGN_REQUIRED_TASK_CODE
  | typeof SUPERVISOR_EDIT_REQUESTED_TASK_CODE
  | typeof TREATMENT_PLAN_DUE_TASK_CODE
  | typeof TREATMENT_PLAN_RENEWAL_TASK_CODE
  | typeof APPOINTMENT_REQUEST_TASK_CODE
  | typeof CLIENT_FORM_ASSIGNMENT_CODE
  | typeof CLIENT_ASSESSMENT_ASSIGNMENT_CODE
  | typeof CLIENT_SIGNATURE_REQUEST_CODE
  | typeof CLIENT_DOCUMENT_COMPLETION_CODE
  | typeof CLAIM_ERROR_TASK_CODE;

/** Purpose category on Task.code (TASK_CATEGORY_SYSTEM). */
export type TaskCategoryCode = 'note' | 'document' | 'client' | 'admin';

/** Supervision task type codes (SUPERVISION_TASK_SYSTEM). */
export type SupervisionTaskTypeCode =
  | 'missing-notes'
  | 'missing-forms'
  | 'cosign-review'
  | 'missing-document';

/** Supervision form subtype codes (SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM). */
export type SupervisionFormSubtypeCode =
  | 'progress-note'
  | 'intake-note'
  | 'treatment-plan'
  | 'discharge-summary';

/** Supervision task category codes (SUPERVISION_TASK_CATEGORY_SYSTEM). */
export type SupervisionTaskCategoryCode = 'missing-document';

// ═══════════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Get the general task type code from code.coding (TASK_TYPE_SYSTEM). */
export function getTaskTypeCode(resource: Task): TaskTypeCode | undefined {
  return resource.code?.coding?.find((c) => c.system === TASK_TYPE_SYSTEM)?.code as TaskTypeCode | undefined;
}

/** Get the supervision task type code from code.coding (SUPERVISION_TASK_SYSTEM). */
export function getSupervisionTaskType(resource: Task): SupervisionTaskTypeCode | undefined {
  return resource.code?.coding?.find((c) => c.system === SUPERVISION_TASK_SYSTEM)?.code as SupervisionTaskTypeCode | undefined;
}

/** Get the supervision form subtype code from code.coding. */
export function getSupervisionFormSubtype(resource: Task): SupervisionFormSubtypeCode | undefined {
  return resource.code?.coding?.find((c) => c.system === SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM)?.code as SupervisionFormSubtypeCode | undefined;
}

/** Get the supervision task category from meta.tag. */
export function getSupervisionTaskCategory(resource: Task): SupervisionTaskCategoryCode | undefined {
  return resource.meta?.tag?.find((t) => t.system === SUPERVISION_TASK_CATEGORY_SYSTEM)?.code as SupervisionTaskCategoryCode | undefined;
}

/** Get the due date (restriction.period.end). */
export function getDueDate(resource: Task): string | undefined {
  return resource.restriction?.period?.end;
}

/** Get the patient reference (Task.for). */
export function getPatientRef(resource: Task): Reference | undefined {
  return resource.for;
}

/** Get the owner (assigned practitioner) reference. */
export function getOwnerRef(resource: Task): Reference | undefined {
  return resource.owner;
}

/** Get the EpisodeOfCare context reference from extensions. */
export function getTaskEpisodeOfCareContext(resource: Task): Reference | undefined {
  return resource.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Setter helpers
// ═══════════════════════════════════════════════════════════════════════════

function setExtension(resource: Task, ext: Extension): void {
  if (!resource.extension) resource.extension = [];
  const idx = resource.extension.findIndex((e) => e.url === ext.url);
  if (idx >= 0) {
    resource.extension[idx] = ext;
  } else {
    resource.extension.push(ext);
  }
}

/** Set the general task type coding. */
export function setTaskTypeCode(resource: Task, code: TaskTypeCode, display?: string): void {
  if (!resource.code) resource.code = {};
  if (!resource.code.coding) resource.code.coding = [];
  const existing = resource.code.coding.find((c) => c.system === TASK_TYPE_SYSTEM);
  if (existing) {
    existing.code = code;
    if (display) existing.display = display;
  } else {
    resource.code.coding.push({ system: TASK_TYPE_SYSTEM, code, display });
  }
}

/** Set the supervision task type coding. */
export function setSupervisionTaskType(resource: Task, code: SupervisionTaskTypeCode, display?: string): void {
  if (!resource.code) resource.code = {};
  if (!resource.code.coding) resource.code.coding = [];
  const existing = resource.code.coding.find((c) => c.system === SUPERVISION_TASK_SYSTEM);
  if (existing) {
    existing.code = code;
    if (display) existing.display = display;
  } else {
    resource.code.coding.push({ system: SUPERVISION_TASK_SYSTEM, code, display });
  }
}

/** Set the supervision form subtype coding. */
export function setSupervisionFormSubtype(resource: Task, code: SupervisionFormSubtypeCode, display?: string): void {
  if (!resource.code) resource.code = {};
  if (!resource.code.coding) resource.code.coding = [];
  const existing = resource.code.coding.find((c) => c.system === SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM);
  if (existing) {
    existing.code = code;
    if (display) existing.display = display;
  } else {
    resource.code.coding.push({ system: SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM, code, display });
  }
}

/** Set the supervision task category meta tag. */
export function setSupervisionTaskCategory(resource: Task, code: SupervisionTaskCategoryCode): void {
  if (!resource.meta) resource.meta = {};
  if (!resource.meta.tag) resource.meta.tag = [];
  const existing = resource.meta.tag.find((t) => t.system === SUPERVISION_TASK_CATEGORY_SYSTEM);
  if (existing) {
    existing.code = code;
  } else {
    resource.meta.tag.push({ system: SUPERVISION_TASK_CATEGORY_SYSTEM, code });
  }
}

/** Set the due date (restriction.period.end). */
export function setDueDate(resource: Task, date: string): void {
  if (!resource.restriction) resource.restriction = {};
  if (!resource.restriction.period) resource.restriction.period = {};
  resource.restriction.period.end = date;
}

/** Set the EpisodeOfCare context reference. */
export function setTaskEpisodeOfCareContext(resource: Task, ref: Reference): void {
  setExtension(resource, { url: EPISODE_OF_CARE_CONTEXT_EXT, valueReference: ref });
}

const INVENTORY_CATEGORY_BY_TYPE: Record<string, TaskCategoryCode> = {
  [MISSING_NOTE_TASK_CODE]: 'note',
  [COSIGN_REQUIRED_TASK_CODE]: 'note',
  [SUPERVISOR_EDIT_REQUESTED_TASK_CODE]: 'note',
  [TREATMENT_PLAN_DUE_TASK_CODE]: 'document',
  [TREATMENT_PLAN_RENEWAL_TASK_CODE]: 'document',
  [APPOINTMENT_REQUEST_TASK_CODE]: 'client',
  [CLIENT_FORM_ASSIGNMENT_CODE]: 'client',
  [CLIENT_ASSESSMENT_ASSIGNMENT_CODE]: 'client',
  [CLIENT_TAKE_NOW_ASSESSMENT_CODE]: 'client',
  [CLIENT_SIGNATURE_REQUEST_CODE]: 'client',
  [CLIENT_DOCUMENT_COMPLETION_CODE]: 'client',
  [CLAIM_ERROR_TASK_CODE]: 'admin',
};

export function taskCategoryForType(typeCode: string): TaskCategoryCode | undefined {
  return INVENTORY_CATEGORY_BY_TYPE[typeCode];
}

export function isSystemManagedTask(resource: Task): boolean {
  return (
    resource.meta?.tag?.some(
      (t) => t.system === TASK_LIFECYCLE_SYSTEM && t.code === TASK_LIFECYCLE_SYSTEM_MANAGED_CODE,
    ) ?? false
  );
}

export function isClientFacingTask(resource: Task): boolean {
  return (
    resource.meta?.tag?.some(
      (t) => t.system === CLIENT_FACING_TASK_TAG_SYSTEM && t.code === CLIENT_FACING_TASK_TAG_CODE,
    ) ?? false
  );
}

export function getTaskCategoryCode(resource: Task): TaskCategoryCode | undefined {
  return resource.code?.coding?.find((c) => c.system === TASK_CATEGORY_SYSTEM)?.code as
    | TaskCategoryCode
    | undefined;
}

function upsertTag(resource: Task, system: string, code: string, display?: string): void {
  if (!resource.meta) resource.meta = {};
  if (!resource.meta.tag) resource.meta.tag = [];
  const existing = resource.meta.tag.find((t) => t.system === system);
  if (existing) {
    existing.code = code;
    if (display) existing.display = display;
  } else {
    resource.meta.tag.push({ system, code, display });
  }
}

function upsertCoding(resource: Task, system: string, code: string, display?: string): void {
  if (!resource.code) resource.code = {};
  if (!resource.code.coding) resource.code.coding = [];
  const existing = resource.code.coding.find((c) => c.system === system);
  if (existing) {
    existing.code = code;
    if (display) existing.display = display;
  } else {
    resource.code.coding.push({ system, code, display });
  }
}

/** Stamp dual category+type coding, system-managed + notification tags, Unify requester. */
export function applySystemManagedTaskSchema(
  resource: Task,
  params: {
    typeCode: string;
    typeDisplay?: string;
    category?: TaskCategoryCode;
    requesterOrgId: string;
    portal?: boolean;
    accounts?: Reference[];
  },
): Task {
  const category = params.category ?? taskCategoryForType(params.typeCode);
  if (category) {
    upsertCoding(resource, TASK_CATEGORY_SYSTEM, category);
  }
  upsertCoding(resource, TASK_TYPE_SYSTEM, params.typeCode, params.typeDisplay);
  upsertTag(resource, TASK_LIFECYCLE_SYSTEM, TASK_LIFECYCLE_SYSTEM_MANAGED_CODE);
  if (category) {
    upsertTag(resource, NOTIFICATION_CATEGORY_SYSTEM, category);
  }
  if (params.portal) {
    upsertTag(resource, CLIENT_FACING_TASK_TAG_SYSTEM, CLIENT_FACING_TASK_TAG_CODE);
  }
  resource.requester = { reference: `Organization/${params.requesterOrgId}` };
  if (params.accounts?.length) {
    resource.meta = { ...resource.meta, accounts: params.accounts };
  }
  resource.meta = withUnifyProfile(resource).meta;
  return resource;
}
